import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Calendar, CheckCheck, Copy, Eye, Send, X } from 'lucide-react';
import axios from 'axios';
import { useAuth } from '../../context/AuthContext';
import {
  Contact,
  EmailTemplate,
  EventBuildResult,
  EventCampaignLink,
  HostlinksEvent,
  InterspireSegment,
} from '../../types';
import { eventsApi } from '../../services/api';
import { getApiErrorParts, logSafeAxiosError } from '../../utils/safeAxiosLog';
import HostlinksEventPicker from './HostlinksEventPicker';
import TemplateCardPicker, { MAX_TEMPLATES } from './TemplateCardPicker';
import ContentPlaceholdersPanel from './ContentPlaceholdersPanel';
import ScheduleTimeline from './ScheduleTimeline';
import CampaignLivePreview, { PreviewItem } from './CampaignLivePreview';
import {
  addDays,
  combineSchedule,
  ContentFields,
  emailsFromTemplate,
  emptyContentFields,
  leftoversFromContent,
  nextEmailId,
  processSubject,
  interspireCampaignName,
  renderContentPreview,
  contact1FromMarketer,
  TemplateBuild,
  ymd,
} from './eventBuildUtils';

const UnifiedBuildCampaign: React.FC = () => {
  const { user } = useAuth();
  const isAdmin = user?.role === 'admin';

  const [event, setEvent] = useState<HostlinksEvent | null>(null);

  const [templates, setTemplates] = useState<EmailTemplate[]>([]);
  const [templatesError, setTemplatesError] = useState<string | null>(null);
  const [segments, setSegments] = useState<InterspireSegment[]>([]);
  const [contacts, setContacts] = useState<Contact[]>([]);
  const [interspireEnabled, setInterspireEnabled] = useState(false);

  const [builds, setBuilds] = useState<TemplateBuild[]>([]);
  const [content, setContent] = useState<ContentFields>(emptyContentFields());

  const [existingLinks, setExistingLinks] = useState<EventCampaignLink[]>([]);
  const [linksLoading, setLinksLoading] = useState(false);

  const [previewEmails, setPreviewEmails] = useState<string[]>([]);
  const [selectedPreviewEmail, setSelectedPreviewEmail] = useState('');
  const [sendingTest, setSendingTest] = useState(false);
  const [previewItems, setPreviewItems] = useState<PreviewItem[] | null>(null);

  const [submitting, setSubmitting] = useState(false);
  const [results, setResults] = useState<EventBuildResult[] | null>(null);
  const [submitError, setSubmitError] = useState<{ headline: string; detail?: string } | null>(null);

  const [showQueueModal, setShowQueueModal] = useState(false);
  const [isCalculating, setIsCalculating] = useState(false);
  const [isQueuing, setIsQueuing] = useState(false);
  const [calculationResult, setCalculationResult] = useState<any>(null);
  const [isStep1Complete, setIsStep1Complete] = useState(false);
  const [manualQueueBuildIndex, setManualQueueBuildIndex] = useState(0);

  const [sendingToCount, setSendingToCount] = useState<number | null>(null);
  const [sendingToLoading, setSendingToLoading] = useState(false);
  const [sendingToCopied, setSendingToCopied] = useState(false);

  const selectedIds = builds.map((b) => b.templateId);

  useEffect(() => {
    axios
      .get<EmailTemplate[]>('/api/templates')
      .then((r) => {
        setTemplates((r.data ?? []).filter((t) => t.kind !== 'zoom'));
        setTemplatesError(null);
      })
      .catch((err) => {
        logSafeAxiosError('Error fetching templates', err);
        const status = axios.isAxiosError(err) ? err.response?.status : undefined;
        setTemplatesError(
          status === 403
            ? 'You do not have permission to view email templates. Ask an admin to enable emailTemplates or buildCampaign for your role.'
            : 'Could not load templates. Try refreshing the page.'
        );
        setTemplates([]);
      });
    axios.get<Contact[]>('/api/contacts').then((r) => setContacts(r.data)).catch(() => setContacts([]));
    axios
      .get<string[]>('/api/settings/preview-emails')
      .then((r) => {
        setPreviewEmails(r.data ?? []);
        if (r.data?.[0]) setSelectedPreviewEmail(r.data[0]);
      })
      .catch(() => {});
    // Load segments directly (same as Templates / Domain Lookup). Do not gate on
    // GET /api/health — that returns 503 when any DB is degraded and axios rejects,
    // which incorrectly disabled segment pickers even when Interspire segments work.
    axios
      .get<InterspireSegment[]>('/api/interspire/segments')
      .then((r) => {
        setSegments(Array.isArray(r.data) ? r.data : []);
        setInterspireEnabled(true);
      })
      .catch(() => {
        setSegments([]);
        setInterspireEnabled(false);
      });
  }, []);

  const loadCampaigns = useCallback(async (eventId: string) => {
    setLinksLoading(true);
    try {
      const res = await eventsApi.getCampaigns(eventId);
      setExistingLinks(res.data ?? []);
    } catch (error) {
      logSafeAxiosError('Error fetching event campaigns', error);
      setExistingLinks([]);
    } finally {
      setLinksLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!event) {
      setExistingLinks([]);
      return;
    }
    setContent((prev) => ({
      ...prev,
      LOCATION: event.location || '',
      DATE: event.dates_display || '',
      LINK: event.web_url || '',
      INTRO: event.host_name || '',
      CONTACT_1: contact1FromMarketer(event.marketer || '', contacts),
    }));
    setBuilds((prev) =>
      prev.map((b) => {
        const template = templates.find((t) => t.id === b.templateId);
        if (!template) return b;
        return {
          ...b,
          emails: b.emails.map((email) => ({
            ...email,
            subject: processSubject(
              template.subjectLines[email.partIndex] ?? email.subject,
              event.location || '',
              event.dates_display || ''
            ),
          })),
        };
      })
    );
    setResults(null);
    setSendingToCount(null);
    setSubmitError(null);
    void loadCampaigns(event.id);
  }, [event, loadCampaigns, templates, contacts]);

  const toggleTemplate = (template: EmailTemplate) => {
    const selected = builds.some((b) => b.templateId === template.id);
    if (selected) {
      setBuilds((prev) => prev.filter((b) => b.templateId !== template.id));
      return;
    }
    if (builds.length >= MAX_TEMPLATES) {
      window.alert(`Maximum ${MAX_TEMPLATES} templates per build.`);
      return;
    }
    const newBuild: TemplateBuild = {
      templateId: template.id,
      segmentId: template.defaultSegmentId ?? null,
      emails: emailsFromTemplate(template, content.LOCATION, content.DATE),
    };
    setBuilds((prev) => [...prev, newBuild]);
  };

  const updateBuild = (templateId: string, patch: Partial<TemplateBuild>) => {
    setBuilds((prev) => prev.map((b) => (b.templateId === templateId ? { ...b, ...patch } : b)));
  };

  const updateEmail = (
    templateId: string,
    emailId: string,
    patch: Partial<{ subject: string; scheduledDate: Date | null; scheduledTime: string }>
  ) => {
    setBuilds((prev) =>
      prev.map((b) =>
        b.templateId === templateId
          ? { ...b, emails: b.emails.map((e) => (e.id === emailId ? { ...e, ...patch } : e)) }
          : b
      )
    );
  };

  const handleFirstDate = (templateId: string, date: Date | null) => {
    if (!date) return;
    setBuilds((prev) =>
      prev.map((b) => {
        if (b.templateId !== templateId) return b;
        return {
          ...b,
          emails: b.emails.map((email, index) => ({
            ...email,
            scheduledDate: addDays(date, index * 14),
          })),
        };
      })
    );
  };

  const addEmail = (templateId: string) => {
    setBuilds((prev) =>
      prev.map((b) => {
        if (b.templateId !== templateId || b.emails.length >= 10) return b;
        const last = b.emails[b.emails.length - 1];
        return {
          ...b,
          emails: [
            ...b.emails,
            {
              id: nextEmailId(),
              partIndex: last?.partIndex ?? 0,
              subject: last?.subject ?? '',
              scheduledDate: last?.scheduledDate ? addDays(last.scheduledDate, 14) : null,
              scheduledTime: last?.scheduledTime || '06:00',
            },
          ],
        };
      })
    );
  };

  const removeEmail = (templateId: string, emailId: string) => {
    setBuilds((prev) =>
      prev.map((b) => {
        if (b.templateId !== templateId || b.emails.length <= 1) return b;
        return { ...b, emails: b.emails.filter((e) => e.id !== emailId) };
      })
    );
  };

  const updateSubjects = (templateId: string) => {
    const template = templates.find((t) => t.id === templateId);
    if (!template) return;
    setBuilds((prev) =>
      prev.map((b) => {
        if (b.templateId !== templateId) return b;
        return {
          ...b,
          emails: b.emails.map((email) => ({
            ...email,
            subject: processSubject(
              template.subjectLines[email.partIndex] ?? email.subject,
              content.LOCATION,
              content.DATE
            ),
          })),
        };
      })
    );
  };

  const previewFor = useCallback(
    (templateId: string, email: { subject: string; partIndex: number }) => {
      const template = templates.find((t) => t.id === templateId);
      if (!template) return null;
      return renderContentPreview(template, content, email.subject, email.partIndex);
    },
    [templates, content]
  );

  const groupedLinks = useMemo(() => {
    const groups = new Map<string, EventCampaignLink[]>();
    for (const link of existingLinks) {
      const key = link.templateName || link.templateId || 'Unknown template';
      const list = groups.get(key) ?? [];
      list.push(link);
      groups.set(key, list);
    }
    return Array.from(groups.entries());
  }, [existingLinks]);

  const firstQueueSegmentId = results?.[0]?.segmentId ?? builds[0]?.segmentId ?? null;
  const firstQueueSegmentName =
    firstQueueSegmentId != null
      ? segments.find((s) => s.segmentid === firstQueueSegmentId)?.segmentname
      : undefined;

  useEffect(() => {
    if (!results?.length || firstQueueSegmentId == null || sendingToCount != null) return;
    let cancelled = false;
    setSendingToLoading(true);
    axios
      .get<{ count: number }>(`/api/interspire/segments/${firstQueueSegmentId}/size`, {
        timeout: 120_000,
      })
      .then((res) => {
        const n = Number(res.data.count);
        if (!cancelled && Number.isFinite(n)) setSendingToCount(n);
      })
      .catch((err) => {
        logSafeAxiosError('Error loading first-card send count', err);
      })
      .finally(() => {
        if (!cancelled) setSendingToLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [results, firstQueueSegmentId, sendingToCount]);

  const copySendingTo = async () => {
    if (sendingToCount == null) return;
    const text = `Sending to ${sendingToCount.toLocaleString()}`;
    try {
      await navigator.clipboard.writeText(text);
      setSendingToCopied(true);
      window.setTimeout(() => setSendingToCopied(false), 2000);
    } catch {
      window.alert('Could not copy.');
    }
  };

  const validateBuilds = (): string | null => {
    if (builds.length === 0) return 'Select at least one template.';
    for (const b of builds) {
      const template = templates.find((t) => t.id === b.templateId);
      if (!b.segmentId) {
        return `Choose a segment for "${template?.name ?? b.templateId}".`;
      }
      const ready = b.emails.filter((e) => e.scheduledDate && e.scheduledTime && e.subject.trim());
      if (ready.length === 0) {
        return `Set subject, date, and time for at least one email on "${template?.name ?? b.templateId}".`;
      }
      const now = Date.now();
      for (const email of ready) {
        const when = combineSchedule(email.scheduledDate!, email.scheduledTime);
        if (when.getTime() < now) {
          const label = `${template?.name ?? 'Template'} · email ${email.partIndex + 1}`;
          return `${label} is scheduled ${when.toLocaleString()} — that is in the past. Choose a date and time after now.`;
        }
      }
    }
    return null;
  };

  const openPreviewFor = (templateIds: string[]) => {
    const items: PreviewItem[] = [];
    for (const id of templateIds) {
      const template = templates.find((t) => t.id === id);
      const build = builds.find((b) => b.templateId === id);
      if (!template) continue;
      const emails = build?.emails?.length
        ? build.emails
        : [{ subject: template.subjectLines[0] ?? '', partIndex: 0 }];
      for (let i = 0; i < emails.length; i++) {
        const preview = previewFor(id, emails[i]);
        if (!preview) continue;
        items.push({
          templateName: emails.length > 1 ? `${template.name} · email ${i + 1}` : template.name,
          subject: preview.subject,
          html: preview.html,
        });
      }
    }
    if (items.length === 0) {
      window.alert('Select a template to preview.');
      return;
    }
    setPreviewItems(items);
  };

  const sendTest = async () => {
    if (builds.length === 0) {
      window.alert('Select at least one template.');
      return;
    }
    const to = selectedPreviewEmail.trim();
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(to)) {
      window.alert('Enter a valid test recipient email address.');
      return;
    }
    const messages: Array<{ subject: string; html: string }> = [];
    const names: string[] = [];
    const missingHtml: string[] = [];
    const total = builds.reduce((n, b) => n + b.emails.length, 0);
    let n = 0;
    for (const b of builds) {
      const template = templates.find((t) => t.id === b.templateId);
      if (!template) continue;
      const partCount = b.emails.length;
      for (const email of b.emails) {
        n += 1;
        const preview = previewFor(b.templateId, email);
        const label = partCount > 1 ? `${template.name} (${email.partIndex + 1})` : template.name;
        let html = preview?.html?.trim() ?? '';
        if (!html) {
          missingHtml.push(label);
          html = `<p>EmailCraft test: no HTML stored for ${label}.</p>`;
        }
        html += `\n<!-- emailcraft-test ${n}/${total} ${Date.now()} -->`;
        messages.push({
          subject: `[TEST ${n}/${total}] ${label} — ${preview?.subject || email.subject}`.slice(0, 500),
          html,
        });
        names.push(label);
      }
    }
    if (messages.length === 0) {
      window.alert('Nothing to send. Select templates with HTML.');
      return;
    }
    setSendingTest(true);
    try {
      await axios.post('/api/email/preview-batch', { to, messages }, { timeout: 120_000 });
      const missingNote = missingHtml.length
        ? `\n\nThese parts had no HTML in the template (sent a placeholder): ${missingHtml.join(', ')}.`
        : '';
      window.alert(`Sent ${messages.length} test email(s) to ${to}: ${names.join(', ')}.${missingNote}`);
    } catch (error) {
      logSafeAxiosError('Error sending test email', error);
      const detail = axios.isAxiosError(error)
        ? error.response?.data?.error || error.message
        : error instanceof Error
          ? error.message
          : 'Unknown error';
      window.alert(`Failed to send test email: ${detail}`);
    } finally {
      setSendingTest(false);
    }
  };

  const submitEventBuilds = async () => {
    if (!event || !isAdmin) return;
    const err = validateBuilds();
    if (err) {
      window.alert(err);
      return;
    }

    const payloadBuilds = builds.map((b) => {
      const ready = b.emails.filter((e) => e.scheduledDate && e.scheduledTime && e.subject.trim());
      return {
        templateId: b.templateId,
        segmentId: b.segmentId!,
        overrides: leftoversFromContent(content),
        schedule: ready.map((e) => ({
          subject: e.subject,
          date: ymd(e.scheduledDate!),
          time: e.scheduledTime,
          scheduledDateIso: e.scheduledDate!.toISOString(),
          partIndex: e.partIndex,
        })),
      };
    });

    setSubmitting(true);
    setSubmitError(null);
    setResults(null);
    setSendingToCount(null);
    try {
      const res = await eventsApi.buildCampaigns(event.id, {
        eventSnapshot: {
          location: event.location,
          dates_display: event.dates_display,
          reg_url: event.reg_url,
          web_url: event.web_url,
          host_name: event.host_name,
        },
        builds: payloadBuilds,
      });
      setResults(res.data.results ?? []);
      await loadCampaigns(event.id);
    } catch (error) {
      logSafeAxiosError('Error building event campaigns', error);
      setSubmitError(getApiErrorParts(error, 'Failed to queue campaigns'));
    } finally {
      setSubmitting(false);
    }
  };

  const openManualQueue = () => {
    if (!isAdmin) return;
    const err = validateBuilds();
    if (err) {
      window.alert(err);
      return;
    }
    setManualQueueBuildIndex(0);
    setCalculationResult(null);
    setIsStep1Complete(false);
    setShowQueueModal(true);
  };

  const handleQueueClick = () => {
    if (event) void submitEventBuilds();
    else openManualQueue();
  };

  const currentManualBuild = builds[manualQueueBuildIndex] ?? null;
  const currentManualTemplate = currentManualBuild
    ? templates.find((t) => t.id === currentManualBuild.templateId)
    : null;

  const handleCalculateManual = async () => {
    if (!currentManualBuild || !currentManualTemplate) return;
    setIsCalculating(true);
    try {
      const [smtpRes, emailConfigRes] = await Promise.all([
        axios.get('/api/settings/smtp'),
        axios.get('/api/settings/email-config').catch(() => ({ data: {} })),
      ]);
      const smtpSettings = smtpRes.data;
      const emailConfig = emailConfigRes.data as { sendFromName?: string };
      if (!smtpSettings.fromEmail) {
        window.alert('Configure SMTP From Email in Admin before calculating.');
        return;
      }
      const fromName = emailConfig.sendFromName || smtpSettings.fromEmail.split('@')[0];
      const ready = currentManualBuild.emails.filter(
        (email) => email.scheduledDate && email.scheduledTime && email.subject.trim()
      );
      const campaigns = ready.map((email) => ({
        campaignName: interspireCampaignName(
          email.partIndex,
          content.LOCATION,
          content.DATE,
          currentManualTemplate.kind
        ),
        subject: processSubject(email.subject, content.LOCATION, content.DATE),
        htmlContent: renderContentPreview(
          currentManualTemplate,
          content,
          email.subject,
          email.partIndex
        ).html,
        scheduledDate: email.scheduledDate!.toISOString(),
        scheduledTime: email.scheduledTime,
      }));

      const response = await axios.post('/api/interspire/calculate', {
        campaigns,
        segmentId: currentManualBuild.segmentId,
        fromName,
        fromEmail: smtpSettings.fromEmail,
      });
      if (response.data.success) {
        setCalculationResult(response.data);
        setIsStep1Complete(true);
      } else {
        throw new Error(response.data.message || 'Failed to calculate campaigns');
      }
    } catch (error) {
      console.error(error);
      window.alert(
        `Error calculating campaigns: ${error instanceof Error ? error.message : 'Unknown error'}`
      );
    } finally {
      setIsCalculating(false);
    }
  };

  const handleQueueManual = async () => {
    if (!currentManualBuild || !currentManualTemplate || !calculationResult) return;
    setIsQueuing(true);
    try {
      const [smtpRes, emailConfigRes] = await Promise.all([
        axios.get('/api/settings/smtp'),
        axios.get('/api/settings/email-config').catch(() => ({ data: {} })),
      ]);
      const smtpSettings = smtpRes.data;
      const emailConfig = emailConfigRes.data as { sendFromName?: string };
      const fromName = emailConfig.sendFromName || smtpSettings.fromEmail.split('@')[0];
      const ready = currentManualBuild.emails.filter(
        (email) => email.scheduledDate && email.scheduledTime && email.subject.trim()
      );
      const campaigns = ready.map((email) => ({
        campaignName: interspireCampaignName(
          email.partIndex,
          content.LOCATION,
          content.DATE,
          currentManualTemplate.kind
        ),
        subject: processSubject(email.subject, content.LOCATION, content.DATE),
        htmlContent: renderContentPreview(
          currentManualTemplate,
          content,
          email.subject,
          email.partIndex
        ).html,
        scheduledDate: email.scheduledDate!.toISOString(),
        scheduledTime: email.scheduledTime,
      }));

      const response = await axios.post('/api/interspire/queue', {
        campaigns,
        segmentId: currentManualBuild.segmentId,
        fromName,
        fromEmail: smtpSettings.fromEmail,
      });

      const ok = response.data.success;
      const row: EventBuildResult = {
        templateId: currentManualBuild.templateId,
        segmentId: currentManualBuild.segmentId!,
        ok: !!ok,
        partial: !!response.data.partial,
        newsletterIds: response.data.campaignIds,
        jobIds: response.data.jobIds,
        scheduleResults: response.data.items,
        error: ok ? undefined : response.data.message || response.data.error,
      };
      setResults((prev) => [...(prev ?? []), row]);
      if (manualQueueBuildIndex === 0) {
        const n = Number(calculationResult.segment?.contactCount);
        if (Number.isFinite(n)) setSendingToCount(n);
      }

      if (manualQueueBuildIndex + 1 < builds.length) {
        setManualQueueBuildIndex((i) => i + 1);
        setCalculationResult(null);
        setIsStep1Complete(false);
        window.alert(
          `Queued "${currentManualTemplate.name}". Continue with the next template (${manualQueueBuildIndex + 2} of ${builds.length}).`
        );
      } else {
        setShowQueueModal(false);
        window.alert(`Finished queuing ${builds.length} template(s).`);
      }
    } catch (error) {
      console.error(error);
      window.alert(
        `Error queuing: ${
          axios.isAxiosError(error)
            ? error.response?.data?.error || error.message
            : error instanceof Error
              ? error.message
              : 'Unknown error'
        }`
      );
    } finally {
      setIsQueuing(false);
    }
  };

  const clearEvent = () => {
    setEvent(null);
    setContent((prev) => ({
      ...prev,
      LOCATION: '',
      DATE: '',
      LINK: '',
      INTRO: '',
      CONTACT_1: '',
    }));
  };

  const alreadyQueuedNode = event ? (
    <div className="rounded-md border border-gray-200 dark:border-slate-600 bg-gray-50 dark:bg-slate-900/40 p-2">
      <p className="text-[11px] text-gray-500 dark:text-slate-400">
        {linksLoading
          ? 'Loading queued campaigns…'
          : existingLinks.length === 0
            ? 'No campaigns queued for this event yet.'
            : null}
      </p>
      {!linksLoading && groupedLinks.length > 0 && (
        <ul className="space-y-0.5 text-[11px] text-gray-700 dark:text-slate-300">
          {groupedLinks.map(([group, links]) => (
            <li key={group}>
              <span className="font-medium">{group}</span>
              <span className="text-gray-500 dark:text-slate-400">
                {' '}
                — {links.map((l) => l.campaignName || `#${l.newsletterId}`).join(', ')}
              </span>
            </li>
          ))}
        </ul>
      )}
    </div>
  ) : null;

  return (
    <div className="space-y-4">
      {templatesError && (
        <div className="rounded-md border border-red-200 dark:border-red-800 bg-red-50 dark:bg-red-900/20 p-3 text-sm text-red-900 dark:text-red-200">
          {templatesError}
        </div>
      )}
      {!isAdmin && (
        <div className="rounded-md border border-amber-200 dark:border-amber-800 bg-amber-50 dark:bg-amber-900/20 p-3 text-sm text-amber-900 dark:text-amber-200">
          Only administrators can queue campaigns to Interspire. You can fill in the form, but Queue
          is disabled.
        </div>
      )}

      {/* Top: Event | Templates */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 items-stretch">
        <HostlinksEventPicker
          selected={event}
          onSelect={setEvent}
          onClear={clearEvent}
          alreadyQueued={alreadyQueuedNode}
        />
        <TemplateCardPicker
          templates={templates}
          selectedIds={selectedIds}
          onToggle={toggleTemplate}
          event={event}
        />
      </div>

      {/* Shared content */}
      <ContentPlaceholdersPanel
        content={content}
        onChange={setContent}
        contacts={contacts}
        fromEvent={!!event}
      />

      {/* Per-template columns */}
      {builds.length === 0 ? (
        <div className="rounded-lg border border-dashed border-gray-300 dark:border-slate-600 bg-gray-50/50 dark:bg-slate-900/30 px-4 py-10 text-center text-sm text-gray-500 dark:text-slate-400">
          Select one or two templates above to open schedule columns.
        </div>
      ) : (
        <div
          className={`grid gap-4 items-start ${
            builds.length === 1 ? 'grid-cols-1 max-w-2xl' : 'grid-cols-1 lg:grid-cols-2'
          }`}
        >
          {builds.map((build, index) => {
            const template = templates.find((t) => t.id === build.templateId);
            if (!template) return null;
            return (
              <ScheduleTimeline
                key={build.templateId}
                columnIndex={index}
                templateName={template.name}
                build={build}
                segments={segments}
                interspireEnabled={interspireEnabled}
                onUpdateBuild={(patch) => updateBuild(build.templateId, patch)}
                onUpdateEmail={(emailId, patch) => updateEmail(build.templateId, emailId, patch)}
                onFirstDate={(date) => handleFirstDate(build.templateId, date)}
                onAddEmail={() => addEmail(build.templateId)}
                onRemoveEmail={(emailId) => removeEmail(build.templateId, emailId)}
                onUpdateSubjects={() => updateSubjects(build.templateId)}
                onPreview={() => openPreviewFor([build.templateId])}
              />
            );
          })}
        </div>
      )}

      {submitError && (
        <div className="rounded-md border border-red-200 dark:border-red-800 bg-red-50 dark:bg-red-900/20 p-3 text-sm text-red-800 dark:text-red-200">
          <p className="font-medium">{submitError.headline}</p>
          {submitError.detail && (
            <p className="mt-2 text-xs whitespace-pre-wrap break-words">{submitError.detail}</p>
          )}
        </div>
      )}

      {results && (
        <div className="bg-white dark:bg-slate-800 shadow-sm rounded-lg border border-gray-200 dark:border-slate-700 p-4">
          <div className="flex flex-wrap items-start justify-between gap-3 mb-2">
            <h3 className="text-base font-medium text-gray-900 dark:text-white">Results</h3>
            {(sendingToLoading || sendingToCount != null) && (
              <div className="flex items-center gap-2 rounded-md border border-gray-200 dark:border-slate-600 bg-gray-50 dark:bg-slate-900/50 px-3 py-2">
                <div className="min-w-0">
                  <p className="text-sm font-medium text-gray-900 dark:text-white">
                    {sendingToLoading && sendingToCount == null
                      ? 'Counting send size…'
                      : `Sending to ${sendingToCount?.toLocaleString() ?? '—'}`}
                  </p>
                  {firstQueueSegmentName && (
                    <p className="text-[11px] text-gray-500 dark:text-slate-400 truncate max-w-[16rem]">
                      {firstQueueSegmentName}
                    </p>
                  )}
                </div>
                <button
                  type="button"
                  onClick={() => void copySendingTo()}
                  disabled={sendingToCount == null}
                  title="Copy send count"
                  className="inline-flex items-center gap-1 shrink-0 rounded-md border border-gray-300 dark:border-slate-600 bg-white dark:bg-slate-800 px-2 py-1 text-xs font-medium text-gray-700 dark:text-slate-200 hover:bg-gray-50 dark:hover:bg-slate-700 disabled:opacity-50"
                >
                  {sendingToCopied ? (
                    <>
                      <CheckCheck className="h-3.5 w-3.5 text-green-600" />
                      Copied
                    </>
                  ) : (
                    <>
                      <Copy className="h-3.5 w-3.5" />
                      Copy
                    </>
                  )}
                </button>
              </div>
            )}
          </div>
          {results.some((r) => r.partial) && (
            <div className="mb-3 rounded-md border border-amber-300 dark:border-amber-700 bg-amber-50 dark:bg-amber-900/20 p-3 text-sm text-amber-950 dark:text-amber-100">
              <p className="font-medium">Partially built — do not re-submit the whole batch</p>
              <p className="mt-1 text-xs">
                Some schedule rows already landed in Interspire. Only re-submit emails that failed or
                were skipped.
              </p>
            </div>
          )}
          <ul className="space-y-2 text-sm">
            {results.map((row, idx) => {
              const template = templates.find((t) => t.id === row.templateId);
              const segment = segments.find((s) => s.segmentid === row.segmentId);
              const tone = row.ok
                ? 'text-green-800 dark:text-green-300'
                : row.partial
                  ? 'text-amber-900 dark:text-amber-200'
                  : 'text-red-800 dark:text-red-300';
              return (
                <li key={`${row.templateId}-${row.segmentId}-${idx}`} className={tone}>
                  <div>
                    <span className="font-medium">{template?.name ?? row.templateId}</span>
                    {' → '}
                    {segment?.segmentname ?? `Segment #${row.segmentId}`}
                    {row.ok && !row.partial
                      ? `: queued ${(row.newsletterIds ?? []).join(', ') || 'n/a'}`
                      : row.partial
                        ? ': partially built'
                        : `: ${row.error || 'failed'}`}
                  </div>
                  {row.scheduleResults && row.scheduleResults.length > 0 && (
                    <ul className="mt-1 ml-3 space-y-0.5 text-xs text-gray-700 dark:text-slate-300">
                      {row.scheduleResults.map((sr) => (
                        <li key={`${row.templateId}-${sr.index}`}>
                          Email {sr.index + 1}
                          {sr.subject ? ` “${sr.subject}”` : ''}:{' '}
                          {sr.status === 'ok'
                            ? `queued (newsletter ${sr.newsletterId ?? 'n/a'})`
                            : sr.status === 'skipped'
                              ? `skipped — ${sr.error || 'not attempted'}`
                              : `failed — ${sr.error || 'error'}`}
                        </li>
                      ))}
                    </ul>
                  )}
                </li>
              );
            })}
          </ul>
        </div>
      )}

      {/* Actions — in document flow so the app footer stays visible below */}
      <div className="border border-gray-200 dark:border-slate-700 rounded-lg bg-white dark:bg-slate-900 px-4 py-3 shadow-sm">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div className="flex flex-wrap items-center gap-2">
            <button
              type="button"
              onClick={() => void sendTest()}
              disabled={builds.length === 0 || sendingTest}
              className="inline-flex items-center px-3 py-2 border border-gray-300 dark:border-slate-600 text-sm font-medium rounded-md text-gray-700 dark:text-slate-200 bg-white dark:bg-slate-800 hover:bg-gray-50 dark:hover:bg-slate-700 disabled:opacity-50"
            >
              <Send className="h-4 w-4 mr-1.5" />
              {sendingTest ? 'Sending…' : 'Send Test'}
            </button>
            <input
              type="email"
              list="build-test-recipients"
              value={selectedPreviewEmail}
              onChange={(e) => setSelectedPreviewEmail(e.target.value)}
              placeholder="Test recipient email"
              autoComplete="email"
              className="rounded-md border-gray-300 dark:border-slate-600 bg-white dark:bg-slate-800 text-gray-900 dark:text-white shadow-sm text-sm focus:border-blue-500 focus:ring-blue-500 min-w-[14rem]"
              title="Type any address, or pick a saved preview email"
            />
            <datalist id="build-test-recipients">
              {previewEmails.map((email) => (
                <option key={email} value={email} />
              ))}
            </datalist>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <button
              type="button"
              onClick={() => openPreviewFor(builds.map((b) => b.templateId))}
              disabled={builds.length === 0}
              className="inline-flex items-center px-3 py-2 border border-gray-300 dark:border-slate-600 text-sm font-medium rounded-md text-gray-700 dark:text-slate-200 bg-white dark:bg-slate-800 hover:bg-gray-50 dark:hover:bg-slate-700 disabled:opacity-50"
            >
              <Eye className="h-4 w-4 mr-1.5" />
              Preview All
            </button>
            <button
              type="button"
              onClick={handleQueueClick}
              disabled={!isAdmin || submitting || builds.length === 0}
              className={`inline-flex items-center px-5 py-2.5 border border-transparent text-sm font-medium rounded-md shadow-sm text-white ${
                !isAdmin || submitting || builds.length === 0
                  ? 'bg-gray-400 dark:bg-slate-600 cursor-not-allowed'
                  : 'bg-blue-600 hover:bg-blue-700'
              }`}
            >
              <Calendar className="h-4 w-4 mr-2" />
              {submitting
                ? 'Queuing…'
                : builds.length <= 1
                  ? 'Queue Campaign'
                  : `Queue Campaign (${builds.length} templates will be queued)`}
            </button>
          </div>
        </div>
      </div>

      <CampaignLivePreview
        open={!!previewItems && previewItems.length > 0}
        items={previewItems ?? []}
        onClose={() => setPreviewItems(null)}
      />

      {showQueueModal && currentManualTemplate && currentManualBuild && (
        <div className="fixed inset-0 z-50 flex items-start justify-center bg-gray-900/50 dark:bg-black/60 p-4 overflow-y-auto">
          <div className="bg-white dark:bg-slate-800 rounded-lg shadow-xl w-full max-w-3xl my-8 p-5 border border-gray-200 dark:border-slate-700">
            <div className="flex justify-between items-start gap-3 mb-4">
              <div>
                <h3 className="text-lg font-bold text-gray-900 dark:text-white">Queue to Interspire</h3>
                <p className="text-sm text-gray-600 dark:text-slate-300 mt-1">
                  Template {manualQueueBuildIndex + 1} of {builds.length}:{' '}
                  <span className="font-medium">{currentManualTemplate.name}</span>
                </p>
              </div>
              <button
                type="button"
                onClick={() => setShowQueueModal(false)}
                className="text-gray-400 hover:text-gray-600 dark:hover:text-slate-200"
              >
                <X className="h-6 w-6" />
              </button>
            </div>

            {!isStep1Complete && (
              <div className="bg-blue-50 dark:bg-blue-900/20 rounded-lg p-4 mb-4 border border-blue-100 dark:border-blue-800">
                <div className="flex items-center justify-between gap-3">
                  <div>
                    <h4 className="text-sm font-medium text-blue-900 dark:text-blue-100">
                      Step 1: Calculate
                    </h4>
                    <p className="text-xs text-blue-700 dark:text-blue-300 mt-1">
                      Preview segment size and schedule before queuing.
                    </p>
                  </div>
                  <button
                    type="button"
                    onClick={() => void handleCalculateManual()}
                    disabled={isCalculating}
                    className={`px-4 py-2 rounded-md text-sm font-medium ${
                      isCalculating
                        ? 'bg-gray-300 dark:bg-slate-600 text-gray-500'
                        : 'bg-blue-600 text-white hover:bg-blue-700'
                    }`}
                  >
                    {isCalculating ? 'Calculating…' : 'Calculate'}
                  </button>
                </div>
              </div>
            )}

            {calculationResult && (
              <div className="bg-green-50 dark:bg-green-900/20 rounded-lg p-4 mb-4 text-sm border border-green-100 dark:border-green-800">
                <p className="font-medium text-green-900 dark:text-green-100 mb-2">
                  Calculation ready
                </p>
                <p className="text-green-800 dark:text-green-200">
                  {calculationResult.segment?.contactCount?.toLocaleString?.() ?? '—'} contacts ·{' '}
                  {calculationResult.summary?.totalCampaigns ?? '—'} campaigns
                </p>
              </div>
            )}

            {isStep1Complete && calculationResult && (
              <div className="flex justify-end gap-2">
                <button
                  type="button"
                  onClick={() => setShowQueueModal(false)}
                  className="px-4 py-2 border border-gray-300 dark:border-slate-600 rounded-md text-sm text-gray-700 dark:text-slate-200 bg-white dark:bg-slate-700"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={() => void handleQueueManual()}
                  disabled={isQueuing}
                  className={`px-4 py-2 rounded-md text-sm font-medium text-white ${
                    isQueuing ? 'bg-gray-400' : 'bg-orange-600 hover:bg-orange-700'
                  }`}
                >
                  {isQueuing ? 'Queuing…' : 'Queue this template'}
                </button>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default UnifiedBuildCampaign;
